import { Link } from "react-router-dom";
import { Instagram, Facebook, Youtube, Mail, MapPin, Phone, Clock } from "lucide-react";
import { BRAND, CONTACT, NAV_LINKS } from "@/lib/site";
import { Logo } from "@/components/Logo";

const socials = [
    { icon: Instagram, href: "https://instagram.com", label: "Instagram" },
    { icon: Facebook, href: "https://facebook.com", label: "Facebook" },
    { icon: Youtube, href: "https://youtube.com", label: "YouTube" },
];

export const Footer = () => (
    <footer className="relative overflow-hidden bg-navy" data-testid="site-footer">
        <Logo className="pointer-events-none absolute -bottom-20 left-1/2 h-[420px] w-[420px] -translate-x-1/2 opacity-[0.05]" />
        <div className="relative z-10 mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
            <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
                <div>
                    <span className="flex items-center gap-3">
                        <Logo className="h-12 w-12" />
                        <span className="font-serif text-2xl font-semibold text-ivory">{BRAND.short}</span>
                    </span>
                    <p className="mt-5 max-w-xs text-sm leading-relaxed text-ivory/60">
                        A domestic tours and travel company crafting bespoke journeys across India — with
                        personal care, transparent pricing and round-the-clock support.
                    </p>
                    <div className="mt-6 flex gap-3">
                        {socials.map(({ icon: Icon, href, label }) => (
                            <a
                                key={label}
                                href={href}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={label}
                                data-testid={`footer-social-${label.toLowerCase()}`}
                                className="rounded-full border border-ivory/20 p-2.5 text-ivory/70 transition-all duration-300 hover:border-brass hover:text-brass"
                            >
                                <Icon className="h-4 w-4" />
                            </a>
                        ))}
                    </div>
                </div>

                <div>
                    <h3 className="text-xs font-semibold uppercase tracking-widest2 text-brass">Explore</h3>
                    <ul className="mt-6 space-y-3">
                        {NAV_LINKS.concat({ label: "Book a Tour", to: "/book" }).map((link) => (
                            <li key={link.to}>
                                <Link
                                    to={link.to}
                                    data-testid={`footer-link-${link.label.toLowerCase().replace(" ", "-")}`}
                                    className="text-sm text-ivory/70 transition-colors hover:text-brass"
                                >
                                    {link.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>

                <div>
                    <h3 className="text-xs font-semibold uppercase tracking-widest2 text-brass">Reach Us</h3>
                    <ul className="mt-6 space-y-4 text-sm text-ivory/70">
                        <li className="flex items-start gap-3">
                            <Phone className="mt-0.5 h-4 w-4 shrink-0 text-brass" />
                            <a href={CONTACT.phoneHref} data-testid="footer-phone-link" className="transition-colors hover:text-brass">
                                {CONTACT.phoneDisplay}
                            </a>
                        </li>
                        <li className="flex items-start gap-3">
                            <Mail className="mt-0.5 h-4 w-4 shrink-0 text-brass" />
                            <a href={CONTACT.emailHref} data-testid="footer-email-link" className="transition-colors hover:text-brass">
                                {CONTACT.email}
                            </a>
                        </li>
                        <li className="flex items-start gap-3">
                            <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brass" />
                            {CONTACT.address}
                        </li>
                    </ul>
                </div>

                <div>
                    <h3 className="text-xs font-semibold uppercase tracking-widest2 text-brass">Office Hours</h3>
                    <p className="mt-6 flex items-start gap-3 text-sm text-ivory/70">
                        <Clock className="mt-0.5 h-4 w-4 shrink-0 text-brass" />
                        {CONTACT.hours}
                    </p>
                    <p className="mt-6 text-sm leading-relaxed text-ivory/50">
                        Prefer chat? Tap the WhatsApp button any time — a travel designer replies personally.
                    </p>
                </div>
            </div>

            <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-ivory/10 pt-8 sm:flex-row">
                <p className="text-xs text-ivory/50">© {new Date().getFullYear()} {BRAND.name}. All rights reserved.</p>
                <p className="text-xs uppercase tracking-[0.2em] text-ivory/40">Tours &amp; Travels · India</p>
            </div>
        </div>
    </footer>
);
